import BLE_UUID_TYPES from '../../enums/ble/bleUuidTypes';
import converter from '../../utils/converter';
import helpers from '../../utils/helpers';
import BleClienNotInitializedError from '../errors/bleClientNotInitializedError';
import PairingInfoError from '../errors/pairingInfoError';
import log from '../logger/log';
import BLEClient from './bleClient';
import {IConnectProps, IIqosBleClientOptions, TOnReadingCharacteristics} from './bleClientTypes';

let instance: IqosBLEClient | null = null;

export default class IqosBLEClient extends BLEClient {
    private scpCharacteristic?: BluetoothRemoteGATTCharacteristic;
    private fwuDataCharacteristic?: BluetoothRemoteGATTCharacteristic;
    private fwuControlCharacteristic?: BluetoothRemoteGATTCharacteristic;
    private fwuStatusCharacteristic?: BluetoothRemoteGATTCharacteristic;

    // @ts-expect-error cannot call 'super()' as first statement
    constructor(options?: IIqosBleClientOptions) {
        if (!options && instance) {
            return instance;
        }
        if (!options) {
            throw new BleClienNotInitializedError();
        }
        const bleClientOptions = {
            ...options,
            onReconnectSuccess: async () => {
                await this.onConnect();
                options.onDeviceReconnectSuccess();
            },
        };

        super(bleClientOptions);
        instance = this;
    }

    destroy(): void {
        instance = null;
    }

    connect = async ({isNewDevice, onConnect, onError, onReadingCharacteristics}: IConnectProps): Promise<void> => {
        await this.connectDevice({
            isNewDevice,
            services: [BLE_UUID_TYPES.RRP_SERVICE],
            optionalServices: [BLE_UUID_TYPES.DEVICE_INFORMATION],
            onConnect: async () => {
                try {
                    this.options.onFinishDeviceSelect();
                    await this.onConnect(onReadingCharacteristics);
                    onConnect();
                } catch (e: any) {
                    onError(e);
                }
            },
            onError,
        });
    };

    getFwuServices = async (): Promise<void> => {
        this.fwuDataCharacteristic = await this.getPrimaryServiceCharacteristic(BLE_UUID_TYPES.FW_UPGRADE_DATA);
        this.fwuControlCharacteristic = await this.getPrimaryServiceCharacteristic(BLE_UUID_TYPES.FW_UPGRADE_CONTROL);
        this.fwuStatusCharacteristic = await this.getPrimaryServiceCharacteristic(BLE_UUID_TYPES.FW_UPGRADE_STATUS);
    };

    addScpCharacteristicListener = (handler: (value: DataView) => void): void =>
        this.addCharacteristicListener(this.scpCharacteristic, handler);

    addFwuStatusCharacteristicListener = (handler: (value: DataView) => void): void =>
        this.addCharacteristicListener(this.fwuStatusCharacteristic, handler);

    removeFwuStatusCharacteristicListener = (): void => this.removeCharacteristicListener(this.fwuStatusCharacteristic);

    writeValueToScpCharacteristic = (frame: string, throwError: boolean, doNotSplit?: boolean): Promise<void> =>
        this.writeFrameToCharacteristic(this.scpCharacteristic, frame, {throwError, doNotSplit});

    writeValueToFwuControlCharacteristic = (frame: string, throwError: boolean): Promise<void> =>
        this.writeFrameToCharacteristic(this.fwuControlCharacteristic, frame, {throwError});

    writeValueToFwuDataCharacteristic = (frame: string, throwError: boolean): Promise<void> =>
        this.writeFrameToCharacteristic(this.fwuDataCharacteristic, frame, {throwError, doNotSplit: true});

    readFwuDataCharacteristic = (handler: (value: DataView) => void): Promise<void> =>
        this.readCharacteristic(this.fwuDataCharacteristic, handler);

    private onConnect = async (onReadingCharacteristics?: TOnReadingCharacteristics): Promise<void> => {
        await helpers.timeout(200);

        const GET_SERVICE_CHARACTERISTICS_TIMEOUT_MS = 30_000;
        let getServiceCharacteristicsTimeout;

        try {
            await new Promise(async (res, rej) => {
                getServiceCharacteristicsTimeout = setTimeout(() => {
                    rej(new Error('IqosBLEClient: onConnect, getting service characteristics is timed out'));
                }, GET_SERVICE_CHARACTERISTICS_TIMEOUT_MS);

                try {
                    await this.getPrimaryService(BLE_UUID_TYPES.RRP_SERVICE);
                    await helpers.timeout(200);
                    this.scpCharacteristic = await this.getPrimaryServiceCharacteristic(
                        BLE_UUID_TYPES.SCP_CONTROL_POINT
                    );

                    res(true);
                } catch (error) {
                    rej(error);
                }
            });
        } finally {
            clearTimeout(getServiceCharacteristicsTimeout);
        }

        if (typeof onReadingCharacteristics === 'function') {
            const deviceName = this.getDeviceName();

            await onReadingCharacteristics(deviceName);
        }

        const manufacturerName = await this.ensureDeviceIsPaired();

        log.info(`IqosBLEClient: onConnect, device is paired, manufacturerName: ${manufacturerName}`);

        this.options.onDeviceConnect();
    };

    private readManufacturerName = async (): Promise<string> => {
        const GET_MANUFACTURER_NAME_TIMEOUT_MS = 30_000;
        let getManufacturerNameTimeout;

        try {
            const manufacturerName = await new Promise(async (res, rej) => {
                getManufacturerNameTimeout = setTimeout(() => {
                    rej(
                        new PairingInfoError(
                            'IqosBLEClient: readManufacturerName, getting manufacturer name is timed out'
                        )
                    );
                }, GET_MANUFACTURER_NAME_TIMEOUT_MS);

                try {
                    const deviceInfoService = await this.server!.getPrimaryService(BLE_UUID_TYPES.DEVICE_INFORMATION);
                    const manufacturerCharacteristic = await deviceInfoService?.getCharacteristic(
                        BLE_UUID_TYPES.MANUFACTURER_NAME
                    );
                    const decoder = new TextDecoder('utf-8');
                    const value = await manufacturerCharacteristic?.readValue();
                    const result = decoder.decode(value);

                    res(result);
                } catch (error) {
                    rej(error);
                }
            });

            return manufacturerName as string;
        } catch (e) {
            log.info('IqosBLEClient: readManufacturerName, device is not paired');

            throw e;
        } finally {
            clearTimeout(getManufacturerNameTimeout);
        }
    };

    private ensureDeviceIsPaired = this.readManufacturerName;

    private writeFrameToCharacteristic = async (
        characteristic: BluetoothRemoteGATTCharacteristic | undefined,
        frame: string,
        options: {
            throwError: boolean;
            doNotSplit?: boolean;
        }
    ): Promise<void> => {
        const {throwError, doNotSplit} = options;

        if (doNotSplit) {
            await this.writeValueToCharacteristic(characteristic, frame, throwError);
        } else {
            const chunks = this.splitFrame(frame);

            for (let j = 0; j < chunks.length; j++) {
                const chunk = chunks[j];

                await this.writeValueToCharacteristic(characteristic, chunk, throwError);
            }
        }
    };

    private splitFrame = (frame: string): string[] => {
        const result: string[] = [];

        this.splitChunkRecursive(frame, result);

        return result;
    };

    private splitChunkRecursive = (frame: string, result: string[]): void => {
        const CHUNK_COUNT_LENGTH = 2;
        const CHUNK_LENGTH = 40;
        const chunk = frame.substring(0, CHUNK_LENGTH);

        result.push(chunk);

        const chunkLengthHex = frame.substring(0, CHUNK_COUNT_LENGTH);
        const chunkLengthDec = converter.hex2dec(chunkLengthHex);

        if (+chunkLengthDec > 0 && frame.length > CHUNK_LENGTH) {
            const frameRest = frame.substring(CHUNK_LENGTH);

            this.splitChunkRecursive(frameRest, result);
        }
    };
}
